import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Sparkles, Target, MapPin, TrendingUp, ArrowRight, AlertCircle, Settings2 } from 'lucide-react';
import { formatCurrency, getStageColor } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AIMatchingPage() {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPrefs, setHasPrefs] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recsRes, prefsRes] = await Promise.all([
        axios.get(`${API}/matching/recommendations`, { headers: getAuthHeaders() }),
        axios.get(`${API}/auth/me/preferences`, { headers: getAuthHeaders() })
      ]);
      setRecommendations(recsRes.data);
      setHasPrefs(prefsRes.data && Object.keys(prefsRes.data).length > 0);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8.5) return 'text-green-600';
    if (score >= 7) return 'text-blue-600';
    if (score >= 5.5) return 'text-yellow-600';
    return 'text-muted-foreground';
  };

  const getScoreBg = (score) => {
    if (score >= 8.5) return 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800';
    if (score >= 7) return 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800';
    if (score >= 5.5) return 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800';
    return 'bg-muted border-border';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading recommendations...</div></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="ai-matching-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={20} className="text-amber-500" />
            <h1 className="text-3xl font-bold tracking-tight">AI Deal Recommendations</h1>
          </div>
          <p className="text-muted-foreground">Deals matched to your investment preferences using AI scoring</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/preferences')} data-testid="edit-preferences-btn">
          <Settings2 size={16} className="mr-2" />Edit Preferences
        </Button>
      </div>

      {!hasPrefs && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">Set your investment preferences for better recommendations</p>
                <p className="text-xs text-muted-foreground mt-0.5">Without preferences, you'll see general opportunities ranked by deal quality.</p>
              </div>
              <Button size="sm" onClick={() => navigate('/preferences')} data-testid="set-preferences-btn">
                Set Preferences
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Recommendations</p>
                <p className="text-2xl font-bold font-mono">{recommendations.length}</p>
              </div>
              <Target className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Match Score</p>
                <p className="text-2xl font-bold font-mono">
                  {recommendations.length > 0
                    ? (recommendations.reduce((a, r) => a + r.match_score, 0) / recommendations.length).toFixed(1)
                    : '0.0'}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top Score</p>
                <p className="text-2xl font-bold font-mono text-green-600">
                  {recommendations.length > 0 ? recommendations[0]?.match_score?.toFixed(1) : '—'}
                </p>
              </div>
              <Sparkles className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {recommendations.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Sparkles size={40} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No recommendations available yet.</p>
            <p className="text-sm text-muted-foreground mt-1">Set your investment preferences to receive tailored deal recommendations.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {recommendations.map((rec, idx) => (
            <Card
              key={rec.deal.id}
              className={`cursor-pointer hover:shadow-md transition-shadow border ${getScoreBg(rec.match_score)}`}
              onClick={() => navigate(`/deals/${rec.deal.id}`)}
              data-testid={`recommendation-${rec.deal.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-background border flex items-center justify-center">
                    <span className={`font-mono text-lg font-bold ${getScoreColor(rec.match_score)}`}>{rec.match_score}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm truncate">{rec.deal.name}</h3>
                      <Badge className={`text-[10px] ${getStageColor(rec.deal.stage)}`}>
                        {rec.deal.stage?.replace(/_/g, ' ')}
                      </Badge>
                      {rec.deal.verification_status === 'verified' && (
                        <Badge className="bg-green-100 text-green-700 text-[9px]">Verified</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin size={11} />{rec.deal.country}</span>
                      <span>{rec.deal.sector}</span>
                      <span className="font-mono">{formatCurrency(rec.deal.capital_required)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {rec.match_reasons.map((reason, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">{reason}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Progress value={rec.match_score * 10} className="w-24 h-2" />
                    <p className="text-[10px] text-muted-foreground mt-1 text-right">{rec.match_score}/10 match</p>
                  </div>
                  <ArrowRight size={16} className="text-muted-foreground flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
